import type { CSSProperties } from 'react';
import cn from 'classnames';
import { DividerFormData } from './contracts';

export type DividerProps = DividerFormData & {
  className?: string;
};

/**
 * A horizontal rule an editor can drop between components or as a row of its own. It fills
 * whatever it is dropped into, so a full-width row gives a full-width line and a column gives
 * a column-wide one.
 *
 * Per-instance configuration travels as CSS custom properties rather than inline
 * `border`/`margin` declarations, so `divider.scss` keeps every fallback — an unset colour
 * resolves to the tenant's `--line` token instead of a hardcoded hex.
 */
export default function Divider({ className, lineStyle, thickness, color, width, spacing }: DividerProps) {
  // Annotated rather than asserted: an assertion would also silence a typo'd standard
  // property or a bad value in the same literal, which is exactly what we want checked.
  const style: CSSProperties & Record<`--${string}`, string> = {
    '--divider-thickness': `${thickness}px`,
    '--divider-width': `${width}%`,
    '--divider-spacing': `${spacing}px`,
    ...(color ? { '--divider-color': color } : {}),
  };

  return <hr className={cn('divider', `divider--${lineStyle}`, className)} style={style} />;
}
